import { TextField, Box, Alert } from '@mui/material';
import { useState } from 'react';
import { FieldInput } from 'src/api/process-module';
import { Field } from 'src/models';
import { forceString } from 'src/utils/forceString';
import logger from 'src/log/log';

interface VideoFieldProps {
  field: Field;
  index: number;
  handleFieldChange: (index: number, field: Partial<FieldInput & Partial<Field>>) => void;
}

export const VideoField: React.FC<VideoFieldProps> = ({ field, index, handleFieldChange }) => {
  const [error, setError] = useState<string>('');

  const videoData = (() => {
    const dataSource = field.dataSource;
    if (!dataSource) return { url: '', type: 'youtube' };

    const dataSourceStr = forceString(dataSource);

    // Try to parse as JSON first
    try {
      const parsed = JSON.parse(dataSourceStr);
      return parsed && typeof parsed === 'object' ? parsed : { url: dataSourceStr, type: 'youtube' };
    } catch {
      // If parsing fails, treat as plain URL string
      return { url: dataSourceStr, type: 'youtube' };
    }
  })();

  const validateVideoUrl = (url: string): { isValid: boolean; type: string; error?: string } => {
    if (!url.trim()) {
      return { isValid: true, type: 'youtube' };
    }

    try {
      const urlObj = new URL(url);

      // YouTube validation
      if (url.includes('youtube.com') || url.includes('youtu.be')) {
        const videoId = url.includes('youtu.be')
          ? url.split('/').pop()?.split('?')[0]
          : new URLSearchParams(urlObj.search).get('v');

        if (videoId && videoId.length >= 10) {
          return { isValid: true, type: 'youtube' };
        }
        return { isValid: false, type: 'youtube', error: 'Invalid YouTube URL format' };
      }

      // Vimeo validation
      if (url.includes('vimeo.com')) {
        const videoId = url.split('/').pop()?.split('?')[0];
        if (videoId && /^\d+$/.test(videoId)) {
          return { isValid: true, type: 'vimeo' };
        }
        return { isValid: false, type: 'vimeo', error: 'Invalid Vimeo URL format' };
      }

      // Loom validation - enhanced for the reported bug
      if (url.includes('loom.com')) {
        const match = url.match(/loom\.com\/(?:share|embed)\/([a-zA-Z0-9]+)/);
        if (match && match[1] && match[1].length >= 10) {
          return { isValid: true, type: 'loom' };
        }
        return { isValid: false, type: 'loom', error: 'Invalid Loom URL format' };
      }

      // Generic URL validation for other video platforms
      if (urlObj.protocol === 'http:' || urlObj.protocol === 'https:') {
        return { isValid: true, type: 'generic' };
      }

      return { isValid: false, type: 'generic', error: 'URL must use HTTP or HTTPS protocol' };
    } catch {
      return { isValid: false, type: 'generic', error: 'Invalid URL format' };
    }
  };

  const handleChange = (value: string) => {
    setError('');

    const validation = validateVideoUrl(value);

    if (!validation.isValid && value.trim()) {
      setError(validation.error || 'Invalid video URL');
      logger.warn('Video field validation failed:', { url: value, error: validation.error });
    }

    try {
      const newVideoData = { ...videoData, url: value.trim(), type: validation.type };
      const serializedData = JSON.stringify(newVideoData);

      logger.log('Video field saving data:', {
        fieldId: field.id,
        videoData: newVideoData,
        serialized: serializedData,
      });

      handleFieldChange(index, {
        id: field.id,
        type: field.type,
        label: field.label || undefined,
        name: field.name || undefined,
        dataSource: serializedData,
        dataSourceType: field.dataSourceType || undefined,
      });
    } catch (serializationError) {
      const errorMsg = 'Failed to save video data';
      setError(errorMsg);
      logger.error('Video field serialization error:', serializationError);
    }
  };

  return (
    <Box sx={{ p: 2 }}>
      <TextField
        fullWidth
        label="Video URL"
        value={videoData.url || ''}
        onChange={(e) => handleChange(e.target.value)}
        placeholder="Enter video URL (YouTube, Vimeo, Loom, etc.)"
        error={!!error}
        helperText={error || 'Supports YouTube, Vimeo, Loom, and other video platforms'}
      />
      {error && (
        <Alert severity="warning" sx={{ mt: 1 }}>
          {error}
        </Alert>
      )}
    </Box>
  );
};
